import React, {
  useRef,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useSelector, useDispatch, RootStateOrAny } from "react-redux";
import { useParams, useLocation, useHistory } from "react-router-dom";
import * as Yup from "yup";
import { Form } from "@unform/web";
import { FormHandles, SubmitHandler } from "@unform/core";
import { addBusinessDays, format, eachDayOfInterval } from "date-fns";
import { OptionTypeBase } from "react-select";
import { useValidation } from "hooks";
import ReactGA from "react-ga";
import ReactPixel from "react-facebook-pixel";

import { IUnidade } from "interfaces/unidade";
import { Scaffold } from "components/Shared";
import {
  CreateProspectActions,
  CreateProspectState,
} from "store/ducks/prospect";
import {
  ListBookingActions,
  ListBookingState,
  CreateBookingActions,
  CreateBookingState,
  BookingIdentUserData,
} from "store/ducks/booking";
import { CampaignState } from "store/ducks/campaign";

import { Select, Input } from "components/Shared/Form";
import GTMDataLayer from "utils/GTM-dataLayer";
import * as S from "./styles";
interface IParams {
  idUnidade: string;
}

interface ILocationState {
  state: IUnidade;
}

const dateRange = eachDayOfInterval({
  start: addBusinessDays(new Date(), 1),
  end: addBusinessDays(new Date(), 5),
});

const dateRangeOptions = dateRange.map((date) => ({
  value: format(date, "yyyy-MM-dd"),
  label: format(date, "dd/MM/yyyy"),
}));

export const NewBooking: React.FC = () => {
  const [dateSelected, setDateSelected] = useState<OptionTypeBase | null>(null);

  const { idUnidade } = useParams<IParams>();
  const location: ILocationState = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const formBookingRef = useRef<FormHandles>(null);
  const prospectRequestStarted = useRef(false);

  const { handleFormErrors } = useValidation();

  const {
    data: prospectData,
    loading: prospectLoading,
    error: prospectError,
  } =
    useSelector<RootStateOrAny>(
      (state) => state.createProspect
    ) as CreateProspectState;

  const bookingUser = useSelector(
    (state: RootStateOrAny) => state.bookingIdentUser.data[0]
  ) as BookingIdentUserData | undefined;

  const { data: bookingData, loading: bookingLoading } =
    useSelector<RootStateOrAny>(
      (state) => state.listBooking
    ) as ListBookingState;

  const { loading: createBookingLoading } = useSelector<RootStateOrAny>(
    (state) => state.createBooking
  ) as CreateBookingState;

  const { data: campaignData } = useSelector<RootStateOrAny, CampaignState>(
    (state) => state.campaign
  );

  const { data: tagIndex } = useSelector<RootStateOrAny, CampaignState>(
    (state) => state.fetchTagIndex
  );

  useEffect(() => {
    if (
      !bookingUser ||
      ![bookingUser.nome, bookingUser.cpf, bookingUser.celular, bookingUser.email]
        .every((value) => typeof value === "string" && value.trim() !== "") ||
      prospectRequestStarted.current
    ) {
      return;
    }

    prospectRequestStarted.current = true;

    const prospectPayload = {
      Nome: bookingUser.nome,
      Cpf: bookingUser.cpf.replace(/\D/g, ""),
      Celular: bookingUser.celular,
      Email: bookingUser.email,
      Sexo: 0,
      ProdutosInteresse: [],
      tagIndex,
      IdUnidade: idUnidade,
      ...(campaignData
        ? {
            Source: campaignData.utm_source,
            Medium: campaignData.utm_medium,
            Campaign: campaignData.utm_campaign,
            Content: campaignData.utm_content,
            Term: campaignData.utm_term,
          }
        : {}),
    };

    ReactPixel.fbq("track", "Schedule");
    ReactGA.ga("event", "conversion", {
      send_to: "AW-640319643/8g13CKKN5_ABEJuBqrEC",
    });

    if (location.state) {
      GTMDataLayer.B2CFormDataUser({
        estado: location.state.UF,
        cidade: location.state.Cidade,
        unidade: location.state.Nome,
        nome: bookingUser.nome,
        cpf: prospectPayload.Cpf,
        email: bookingUser.email,
        telefone: bookingUser.celular,
        sexo: 0,
        ProdutosInteresse: [],
      });
    }

    dispatch(CreateProspectActions.request(prospectPayload));
  }, [
    bookingUser,
    campaignData,
    dispatch,
    idUnidade,
    location.state,
    tagIndex,
  ]);

  const onCreateBooking = useCallback(
    (data) => {
      setDateSelected(null);
      history.push({
        pathname: "/avaliacao",
        state: {
          unidade: location.state,
          avaliacao: data,
          prospect: prospectData,
        },
      });
    },
    [history, location.state, prospectData]
  );

  const handleSubmitBooking = useCallback<SubmitHandler>(
    async (data) => {
      try {
        formBookingRef.current?.setErrors({});
        const schema = Yup.object().shape({
          DataHoraAvaliacao: Yup.string().required("Obrigatório"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        if (location.state && bookingUser) {
          GTMDataLayer.B2CFormData({
            estado: location.state.UF,
            cidade: location.state.Cidade,
            unidade: location.state.Nome,
            nome: bookingUser.nome,
            cpf: bookingUser.cpf,
            email: bookingUser.email,
            telefone: bookingUser.celular,
            sexo: 0,
            ProdutosInteresse: prospectData.ProdutosInteresse,
            dataHoraAvaliacao: data.DataHoraAvaliacao,
            idProspect: prospectData.IdProspect,
          });
        }

        data.IdProspect = prospectData.IdProspect;
        data.IdUnidade = prospectData.IdUnidade;
        delete data.Nome;
        delete data.Celular;
        delete data.Email;

        if (campaignData) {
          data.source = campaignData?.utm_source || null;
          data.medium = campaignData?.utm_medium || null;
          data.campaign = campaignData?.utm_campaign || null;
          data.content = campaignData?.utm_content || null;
          data.term = campaignData?.utm_term || null;
        }

        dispatch(CreateBookingActions.request(data, onCreateBooking));
      } catch (error) {
        handleFormErrors(error, formBookingRef);
      }
    },
    [
      bookingUser,
      campaignData,
      dispatch,
      handleFormErrors,
      onCreateBooking,
      prospectData,
    ]
  );

  const getDates = useCallback(
    (date) => {
      dispatch(
        ListBookingActions.request({
          idUnidade: idUnidade,
          tagIndex: tagIndex,
          data: date,
        })
      );
    },
    [dispatch, idUnidade, tagIndex]
  );

  const onDateChange = useCallback(
    (option) => {
      setDateSelected(option);
      getDates(option.value);
    },
    [getDates]
  );

  useEffect(() => {
    return () => {
      dispatch(CreateProspectActions.reset());
    };
  }, [dispatch]);

  const BookingForm: React.FC = () => {
    return (
      <Form ref={formBookingRef} onSubmit={handleSubmitBooking}>
        <Input name="Sexo" type="hidden" value={0} />
        <Input
          name="Cpf"
          label="Digite seu cpf: "
          defaultValue={bookingUser?.cpf}
          disabled
        />
        <Input
          name="Celular"
          label="Celular: "
          defaultValue={bookingUser?.celular}
          disabled
        />
        <Input
          name="Nome"
          label="Digite seu nome: "
          defaultValue={bookingUser?.nome}
          disabled
        />
        <Input
          name="Email"
          label="Email: "
          defaultValue={bookingUser?.email}
          disabled
        />

        <Select
          name="data"
          label="Selecione uma data:"
          options={dateRangeOptions}
          onChange={(e) => onDateChange(e)}
          defaultValue={dateSelected}
        />
        <Select
          name="DataHoraAvaliacao"
          label="Selecione um horário:"
          isDisabled={bookingLoading}
          isLoading={bookingLoading}
          options={bookingData}
        />
        <S.SubmitButton
          id="segunda-parte"
          disabled={
            prospectLoading ||
            !prospectData?.IdProspect ||
            !bookingUser
          }
        >
          Enviar
          {prospectLoading || createBookingLoading ? (
            <S.Loading />
          ) : (
            <S.ArrowRightIcon />
          )}
        </S.SubmitButton>
      </Form>
    );
  };

  return (
    <Scaffold>
      <S.Container>
        <S.Content>
          <S.Wrapper>
            {/* <S.BackButton onClick={() => history.push("/agendar-avaliacao")}> */}
            <S.BackButton onClick={() => history.goBack()}>
              <S.BackgroundIcon>
                <S.BackIcon />
              </S.BackgroundIcon>
              Voltar
            </S.BackButton>
            <S.Title>
              Marque sua
              <span> avaliação</span>
            </S.Title>
            <S.Text>
              Preencha seus dados e agende seu horário na unidade:
              <br />
              <span>{location.state?.Nome}</span>
            </S.Text>
            <S.Aviso>
              Atenção: Não temos convênio ou parceria com o SUS.
            </S.Aviso>

            {!bookingUser && (
              <S.Aviso>
                Não encontramos os dados do usuário. Volte e preencha seus
                dados antes de continuar.
              </S.Aviso>
            )}
            {prospectError && (
              <S.Aviso>
                Não foi possível iniciar o agendamento: {prospectError}
              </S.Aviso>
            )}
            <BookingForm />
          </S.Wrapper>
        </S.Content>
      </S.Container>
    </Scaffold>
  );
};
